/**
 * Контракт «вопрос пользователю» (`ask_user`, этап 1 roadmap 0.13 — блок 1.3).
 *
 * Модель: агент задаёт один или несколько вопросов с вариантами ответа и ждёт
 * решения пользователя. Вопрос — **персистентная запись** (порт
 * `QuestionRepository`, ADR-096): она переживает реконнект UI и рестарт сервиса,
 * поэтому ответ приходит по `id` вопроса, а не «по каналу».
 *
 * Здесь только контракты (типы + Zod-схемы HTTP-входа) — без бизнес-логики:
 * запуск вопроса, ожидание ответа и таймаут живут в core.
 */
import { z } from 'zod';
/** Терминальные статусы вопроса — записи, которые можно чистить (`prune`). */
export const QUESTION_TERMINAL_STATUSES = ['answered', 'cancelled'];
// ── Zod-схемы (HTTP-вход и запись) ─────────────────────────────────────────────
/** Статус вопроса (канонический набор). */
export const questionStatusSchema = z.enum(['pending', 'answered', 'cancelled']);
/** Вариант ответа. */
export const questionOptionSchema = z.object({
    value: z.string().min(1),
    label: z.string().min(1),
    description: z.string().optional(),
});
/** Один вопрос с вариантами. */
export const questionItemSchema = z.object({
    question: z.string().min(1),
    header: z.string().min(1),
    options: z.array(questionOptionSchema),
    multiple: z.boolean().optional(),
});
/**
 * Полная запись вопроса (ответы API). Поле `answers` — массив массивов
 * (`answers[i]` — выбранные значения вариантов для `questions[i]`).
 */
export const questionRecordSchema = z.object({
    id: z.string().min(1),
    sessionId: z.string().min(1),
    turnId: z.string().optional(),
    toolCallId: z.string().optional(),
    agentRole: z.string().optional(),
    questions: z.array(questionItemSchema),
    status: questionStatusSchema,
    answers: z.array(z.array(z.string())).optional(),
    createdAt: z.string().min(1),
    updatedAt: z.string().min(1),
    expiresAt: z.string().optional(),
    answeredAt: z.string().optional(),
    cancelledAt: z.string().optional(),
    cancelReason: z.string().optional(),
    answeredBy: z.string().optional(),
});
/**
 * Тело ответа на вопрос (`POST` ответа пользователя).
 *
 * Длина `answers` равна числу заданных вопросов, каждая строка — список
 * выбранных значений вариантов. Соответствие длины и непустота обязательных
 * ответов проверяет **сервис** (он знает состав вопроса).
 */
export const questionAnswerSchema = z.object({
    answers: z.array(z.array(z.string())),
});
/** Тело отмены вопроса (`reason` — свободное пояснение). */
export const questionCancelSchema = z.object({
    reason: z.string().optional(),
});
